import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";
import type { DetailedHTMLProps, InputHTMLAttributes } from "react";
import { forwardRef } from "react";
import { LoadingSpinner } from "../../feedback";
import { classNames } from "../../utils";

export const selectStyles = cva(
	[
		"block w-full rounded-md border border-input text-neutral focus:border-primary focus:ring-primary disabled:bg-disabled disabled:text-neutral-faded disabled:hover:cursor-not-allowed aria-invalid:border-input-error aria-invalid:ring-critical",
	],
	{
		variants: {
			size: {
				xs: ["text-xs", "py-0.5", "h-7", "leading-tight"],
				sm: ["text-sm", "py-0.5", "h-8", "leading-tight"],
				md: ["text-base", "md:text-sm", "py-2", "h-9", "leading-tight"],
			},
		},
		defaultVariants: {
			size: "md",
		},
	},
);

export type BaseSelectProps = Omit<
	DetailedHTMLProps<InputHTMLAttributes<HTMLSelectElement>, HTMLSelectElement>,
	"size"
> & {
	size?: VariantProps<typeof selectStyles>["size"];
	isLoading?: boolean;
};

const BaseSelectInner = (props: BaseSelectProps, ref: React.ForwardedRef<HTMLSelectElement>) => {
	const { size = "md", isLoading, children, placeholder, ...rest } = props;
	return (
		<div className="relative">
			<select
				ref={ref}
				id={props.name}
				{...rest}
				className={classNames(selectStyles({ size }), props.className, isLoading ? "appearance-none" : "")}
			>
				{placeholder !== undefined && placeholder !== null ? (
					<option value="">{placeholder}</option>
				) : undefined}
				{children}
			</select>
			<div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-8">
				{isLoading ? <LoadingSpinner className="text-primary" /> : undefined}
			</div>
		</div>
	);
};

BaseSelectInner.displayName = "BaseSelect";

export const BaseSelect = forwardRef(BaseSelectInner);
